/**
 * Hoot Hollow - A cozy growing woodland world for owl agents
 * 
 * Vision: Warm, bright, alive - a sanctuary that grows and ages
 * Buildings represented by icons, terrain is simple and readable
 * Agents own parcels, deploy owls, earn $owlSDT
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls, Sky, Html } from '@react-three/drei';
import * as THREE from 'three';
import { ethers } from 'ethers';
import { CONTRACTS, HOOTLAND_AGENT_ABI } from '../contracts';
import WalletConnector from './WalletConnector';

// ============================================
// ENS ERROR SUPPRESSION (Base doesn't support ENS)
// ============================================
if (typeof window !== 'undefined') {
  const originalConsoleError = console.error;
  console.error = function(...args) {
    const msg = args[0]?.toString() || '';
    // Suppress expected ENS errors on Base - they're normal
    if (msg.includes('ENS') || msg.includes('ens')) {
      console.warn('[Expected] ENS not supported on Base');
      return;
    }
    // Don't suppress NFT errors - show them!
    originalConsoleError.apply(console, args);
  };
}

// ============================================
// BASE NETWORK CONFIG
// ============================================
const BASE_CHAIN_ID = 8453;
const BASE_CHAIN_ID_HEX = '0x2105';
const BASE_RPC_URL = 'https://mainnet.base.org';

// ============================================
// CONFIGURATION
// ============================================

const WORLD_SIZE = 80;
const CELL_SIZE = 1;
const MAX_HEIGHT = 24;

// Contract addresses
const HOOTLAND_AGENT_ADDRESS = CONTRACTS.HootlandAgent || '0x4E8C7160ac34fc93a4D16DDa1817A27455809bA2';

// World colors - warm and bright
const COLORS = {
  SKY_TOP: '#87CEEB',
  SKY_BOTTOM: '#E0F6FF',
  SUN: '#FFE4B5',
  GRASS: '#7CB342',
  GRASS_DARK: '#558B2F',
  DIRT: '#8D6E63',
  STONE: '#9E9E9E',
  WOOD: '#5D4037',
  LEAVES: '#4CAF50',
  LEAVES_DARK: '#2E7D32',
  WATER: '#4FC3F7',
  SAND: '#FFE082',
  PATH: '#D7CCC8',
  FLOWER_RED: '#EF5350',
  FLOWER_YELLOW: '#FFEE58',
  FLOWER_BLUE: '#42A5F5',
  MUSHROOM: '#FF8A65',
  OWL_NEST: '#8D6E63',
  BUILDING_STONE: '#BDBDBD',
  ROOF_TILE: '#5D4037',
  OWL_PRIMARY: '#8D6E63',
  OWL_SECONDARY: '#5D4037',
};

// Agent tier colors
const TIER_COLORS = {
  1: '#4A90D9', // Agent - blue
  2: '#9B59B6', // Sentinel - purple
  3: '#E74C3C', // Overseer - red
  4: '#FFD700', // Elder - gold
};

// ============================================
// SIMPLEX NOISE
// ============================================

class SimplexNoise {
  constructor(seed = 12345) {
    this.p = new Uint8Array(256);
    for (let i = 0; i < 256; i++) this.p[i] = i;
    let n, q;
    for (let i = 255; i > 0; i--) {
      seed = (seed * 16807) % 2147483647;
      n = seed % (i + 1);
      q = this.p[i]; this.p[i] = this.p[n]; this.p[n] = q;
    }
    this.perm = new Uint8Array(512);
    for (let i = 0; i < 512; i++) this.perm[i] = this.p[i & 255];
  }
  
  noise2D(x, y) {
    const F2 = 0.5 * (Math.sqrt(3) - 1);
    const G2 = (3 - Math.sqrt(3)) / 6;
    let n0, n1, n2;
    const s = (x + y) * F2;
    const i = Math.floor(x + s);
    const j = Math.floor(y + s);
    const t = (i + j) * G2;
    const X0 = i - t;
    const Y0 = j - t;
    const x0 = x - X0;
    const y0 = y - Y0;
    let i1, j1;
    if (x0 > y0) { i1 = 1; j1 = 0; }
    else { i1 = 0; j1 = 1; }
    const x1 = x0 - i1 + G2;
    const y1 = y0 - j1 + G2;
    const x2 = x0 - 1 + 2 * G2;
    const y2 = y0 - 1 + 2 * G2;
    const ii = i & 255;
    const jj = j & 255;
    const grad = (hash, x, y) => {
      const h = hash & 7;
      const u = h < 4 ? x : y;
      const v = h < 4 ? y : x;
      return ((h & 1) ? -u : u) + ((h & 2) ? -2 * v : 2 * v);
    };
    let t0 = 0.5 - x0 * x0 - y0 * y0;
    if (t0 < 0) n0 = 0;
    else { t0 *= t0; n0 = t0 * t0 * grad(this.perm[ii + this.perm[jj]], x0, y0); }
    let t1 = 0.5 - x1 * x1 - y1 * y1;
    if (t1 < 0) n1 = 0;
    else { t1 *= t1; n1 = t1 * t1 * grad(this.perm[ii + i1 + this.perm[jj + j1]], x1, y1); }
    let t2 = 0.5 - x2 * x2 - y2 * y2;
    if (t2 < 0) n2 = 0;
    else { t2 *= t2; n2 = t2 * t2 * grad(this.perm[ii + 1 + this.perm[jj + 1]], x2, y2); }
    return 70 * (n0 + n1 + n2);
  }
  
  fbm(x, y, octaves = 4) {
    let value = 0, amplitude = 1, frequency = 1, maxValue = 0;
    for (let i = 0; i < octaves; i++) {
      value += amplitude * this.noise2D(x * frequency, y * frequency);
      maxValue += amplitude;
      amplitude *= 0.5;
      frequency *= 2;
    }
    return value / maxValue;
  }
}

// ============================================
// BLOCK TYPES
// ============================================

const BLOCK = {
  AIR: 0,
  GRASS: 1,
  DIRT: 2,
  STONE: 3,
  WOOD: 4,
  LEAVES: 5,
  WATER: 6,
  SAND: 7,
  PATH: 8,
  FLOWER_RED: 9,
  FLOWER_YELLOW: 10,
  FLOWER_BLUE: 11,
  MUSHROOM: 12,
  OWL_NEST: 13,
  BUILDING_BASE: 14,
  ROOF: 15,
};

const BLOCK_COLORS = {
  [BLOCK.GRASS]: COLORS.GRASS,
  [BLOCK.DIRT]: COLORS.DIRT,
  [BLOCK.STONE]: COLORS.STONE,
  [BLOCK.WOOD]: COLORS.WOOD,
  [BLOCK.LEAVES]: COLORS.LEAVES,
  [BLOCK.WATER]: COLORS.WATER,
  [BLOCK.SAND]: COLORS.SAND,
  [BLOCK.PATH]: COLORS.PATH,
  [BLOCK.OWL_NEST]: COLORS.OWL_NEST,
  [BLOCK.BUILDING_BASE]: COLORS.BUILDING_STONE,
  [BLOCK.ROOF]: COLORS.ROOF_TILE,
};

const TRANSPARENT_BLOCKS = new Set([BLOCK.WATER, BLOCK.LEAVES]);

// ============================================
// WORLD GENERATION
// ============================================

function generateHootHollow() {
  const noise = new SimplexNoise(54321);
  const detailNoise = new SimplexNoise(12345);
  
  const blocks = [];
  const entities = [];
  const parcels = [];
  
  for (let x = 0; x < WORLD_SIZE; x++) {
    for (let z = 0; z < WORLD_SIZE; z++) {
      const nx = x / WORLD_SIZE;
      const nz = z / WORLD_SIZE;
      
      let height = Math.floor(
        3 +
        noise.fbm(x / 20, z / 20, 3) * 4 +
        noise.fbm(x / 8, z / 8, 2) * 1.5
      );
      
      const edgeDist = Math.min(x, z, WORLD_SIZE - 1 - x, WORLD_SIZE - 1 - z);
      if (edgeDist < 4) {
        height = Math.max(1, height - (4 - edgeDist) * 1.5);
      }
      
      const worldX = x - WORLD_SIZE / 2;
      const worldZ = z - WORLD_SIZE / 2;
      
      for (let y = 0; y <= height; y++) {
        let blockType;
        if (y === height) {
          const n = detailNoise.noise2D(x * 0.3, z * 0.3);
          const sparseRoll = Math.random();
          if (n > 0.85 && sparseRoll > 0.85) {
            blockType = BLOCK.FLOWER_RED + Math.floor(Math.random() * 3);
          } else if (n > 0.83 && sparseRoll > 0.9) {
            blockType = BLOCK.MUSHROOM;
          } else if (height < 2) {
            blockType = BLOCK.SAND;
          } else {
            blockType = BLOCK.GRASS;
          }
        } else if (y > height - 3) {
          blockType = BLOCK.DIRT;
        } else {
          blockType = BLOCK.STONE;
        }
        
        if (blockType >= 1 && blockType <= 8) {
          blocks.push({ x: worldX, y, z: worldZ, type: blockType });
        } else {
          entities.push({ x: worldX, y: y + 1, z: worldZ, type: blockType, variant: 'decoration' });
        }
      }
      
      if (height < 2 && edgeDist >= 4) {
        const waterNoise = noise.noise2D(x / 12, z / 12);
        if (waterNoise > 0.3) {
          for (let wy = 0; wy <= 1; wy++) {
            blocks.push({ x: worldX, y: wy, z: worldZ, type: BLOCK.WATER });
          }
        }
      }
      
      const treeNoise = noise.noise2D(x * 0.1, z * 0.1);
      if (treeNoise > 0.75 && height >= 3 && Math.random() < 0.02) {
        generateTree(worldX, height + 1, worldZ, blocks, entities, noise);
      }
      
      const distFromCenter = Math.sqrt(Math.pow(x - WORLD_SIZE/2, 2) + Math.pow(z - WORLD_SIZE/2, 2));
      if (distFromCenter < 12 && distFromCenter > 8 && height >= 2) {
        const existingIdx = blocks.findIndex(b => b.x === worldX && b.z === worldZ && b.y === height);
        if (existingIdx !== -1) {
          blocks[existingIdx].type = BLOCK.PATH;
        }
      }
      
      if (distFromCenter > 14 && distFromCenter < 30 && height >= 2) {
        const parcelX = Math.floor(worldX / 4);
        const parcelZ = Math.floor(worldZ / 4);
        const parcelId = `${parcelX}_${parcelZ}`;
        if (!parcels.find(p => p.id === parcelId)) {
          parcels.push({
            id: parcelId,
            x: parcelX * 4,
            z: parcelZ * 4,
            centerX: parcelX * 4 + 2,
            centerZ: parcelZ * 4 + 2,
            owner: null,
            price: 0.001,
            forSale: true,
            y: height + 1
          });
        }
      }
    }
  }
  
  entities.push({
    x: 0, y: 4, z: 0,
    type: BLOCK.OWL_NEST,
    variant: 'owl_sanctuary',
    name: 'Owl Sanctuary',
    description: 'The heart of Hoot Hollow'
  });
  
  const buildingSpots = [
    { x: 18, z: 4, name: 'General Store', type: 'shop' },
    { x: -18, z: -4, name: 'Treehouse Inn', type: 'inn' },
    { x: 4, z: 18, name: 'Garden Plots', type: 'garden' },
    { x: -4, z: -18, name: 'Mining Cave', type: 'mine' },
  ];
  
  buildingSpots.forEach(spot => {
    const worldX = spot.x - WORLD_SIZE / 2;
    const worldZ = spot.z - WORLD_SIZE / 2;
    const groundBlock = blocks.find(b => b.x === worldX && b.z === worldZ);
    if (groundBlock) {
      entities.push({
        x: worldX,
        y: groundBlock.y + 1,
        z: worldZ,
        type: BLOCK.BUILDING_BASE,
        variant: 'building',
        buildingType: spot.type,
        name: spot.name
      });
    }
  });
  
  return { blocks, entities, parcels, height: MAX_HEIGHT };
}

function generateTree(x, y, z, blocks, entities, noise) {
  const height = 3 + Math.floor(Math.abs(noise.noise2D(x, z)) * 2);
  
  for (let h = 0; h < height; h++) {
    blocks.push({ x, y: y + h, z, type: BLOCK.WOOD });
  }
  
  const leafRadius = 1;
  for (let lx = -leafRadius; lx <= leafRadius; lx++) {
    for (let lz = -leafRadius; lz <= leafRadius; lz++) {
      for (let ly = 0; ly <= 2; ly++) {
        const dist = Math.abs(lx) + Math.abs(lz);
        if (dist <= leafRadius + 1 && !(lx === 0 && lz === 0 && ly < 1)) {
          const leafNoise = noise.noise2D(x + lx, z + lz + ly);
          if (leafNoise > -0.5) {
            blocks.push({
              x: x + lx,
              y: y + height + ly,
              z: z + lz,
              type: BLOCK.LEAVES
            });
          }
        }
      }
    }
  }
  
  if (noise.noise2D(x * 3, z * 3) > 0.85) {
    entities.push({
      x: x,
      y: y + height,
      z: z,
      type: BLOCK.OWL_NEST,
      variant: 'tree_nest',
      name: 'Owl Nest'
    });
  }
}

// ============================================
// COMPONENTS
// ============================================

function InstancedBlocks({ blocks, color, transparent }) {
  const meshRef = useRef();
  
  useEffect(() => {
    if (meshRef.current && blocks.length > 0) {
      const matrix = new THREE.Matrix4();
      blocks.forEach((block, i) => {
        matrix.setPosition(block.x, block.y, block.z);
        meshRef.current.setMatrixAt(i, matrix);
      });
      meshRef.current.instanceMatrix.needsUpdate = true;
    }
  }, [blocks]);
  
  if (blocks.length === 0) return null;
  
  return (
    <instancedMesh ref={meshRef} args={[null, null, Math.max(1, blocks.length)]}>
      <boxGeometry args={[0.95, 0.95, 0.95]} />
      <meshStandardMaterial
        color={color}
        transparent={transparent}
        opacity={transparent ? 0.7 : 1}
        roughness={0.8}
        metalness={0.1}
      />
    </instancedMesh>
  );
}

function OwlAgent({ agent, onClick }) {
  const meshRef = useRef();
  const [hovered, setHovered] = useState(false);
  const tierColor = TIER_COLORS[agent.tier] || TIER_COLORS[1];
  
  useFrame((state) => {
    if (meshRef.current) {
      meshRef.current.position.y = agent.y + Math.sin(state.clock.elapsedTime * 2 + agent.x) * 0.1;
      meshRef.current.rotation.y = Math.sin(state.clock.elapsedTime * 0.5) * 0.2;
    }
  });
  
  return (
    <group
      position={[agent.x - WORLD_SIZE/2, agent.y, agent.z - WORLD_SIZE/2]}
      onClick={() => onClick(agent)}
      onPointerOver={() => setHovered(true)}
      onPointerOut={() => setHovered(false)}
    >
      <mesh ref={meshRef}>
        <sphereGeometry args={[0.4, 16, 16]} />
        <meshStandardMaterial
          color={tierColor}
          emissive={tierColor}
          emissiveIntensity={hovered ? 0.5 : 0.2}
        />
      </mesh>
      <mesh position={[0.15, 0.1, 0.3]}>
        <sphereGeometry args={[0.1, 8, 8]} />
        <meshStandardMaterial color="#FFF" />
      </mesh>
      <mesh position={[-0.15, 0.1, 0.3]}>
        <sphereGeometry args={[0.1, 8, 8]} />
        <meshStandardMaterial color="#FFF" />
      </mesh>
      <mesh position={[0.15, 0.1, 0.38]}>
        <sphereGeometry args={[0.05, 6, 6]} />
        <meshStandardMaterial color="#000" />
      </mesh>
      <mesh position={[-0.15, 0.1, 0.38]}>
        <sphereGeometry args={[0.05, 6, 6]} />
        <meshStandardMaterial color="#000" />
      </mesh>
      <mesh position={[0.25, 0.35, 0]}>
        <coneGeometry args={[0.1, 0.2, 4]} />
        <meshStandardMaterial color={tierColor} />
      </mesh>
      <mesh position={[-0.25, 0.35, 0]}>
        <coneGeometry args={[0.1, 0.2, 4]} />
        <meshStandardMaterial color={tierColor} />
      </mesh>
      
      {hovered && (
        <Html position={[0, 1, 0]} center>
          <div style={{
            background: 'rgba(0,0,0,0.85)',
            color: '#fff',
            padding: '8px 12px',
            borderRadius: '8px',
            fontSize: '11px',
            fontFamily: 'system-ui, sans-serif',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            transform: 'translateY(-20px)'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
              🦉 Agent #{agent.id}
            </div>
            <div style={{ color: tierColor }}>
              {['Agent', 'Sentinel', 'Overseer', 'Elder'][agent.tier - 1] || 'Agent'}
            </div>
            <div style={{ marginTop: '4px', color: '#7CB342' }}>
              ⚡ {agent.earnings?.toFixed(4) || '0'} owlSDT/hr
            </div>
          </div>
        </Html>
      )}
    </group>
  );
}

function Decoration({ position, type }) {
  const getDecoration = () => {
    switch (type) {
      case BLOCK.FLOWER_RED:
        return <mesh position={position}><sphereGeometry args={[0.15, 6, 6]} /><meshStandardMaterial color={COLORS.FLOWER_RED} /></mesh>;
      case BLOCK.FLOWER_YELLOW:
        return <mesh position={position}><sphereGeometry args={[0.15, 6, 6]} /><meshStandardMaterial color={COLORS.FLOWER_YELLOW} /></mesh>;
      case BLOCK.FLOWER_BLUE:
        return <mesh position={position}><sphereGeometry args={[0.15, 6, 6]} /><meshStandardMaterial color={COLORS.FLOWER_BLUE} /></mesh>;
      case BLOCK.MUSHROOM:
        return (
          <group position={position}>
            <mesh position={[0, -0.1, 0]}><cylinderGeometry args={[0.05, 0.05, 0.2, 6]} /><meshStandardMaterial color="#EFEBE9" /></mesh>
            <mesh position={[0, 0.05, 0]}><sphereGeometry args={[0.15, 8, 6]} /><meshStandardMaterial color={COLORS.MUSHROOM} /></mesh>
          </group>
        );
      case BLOCK.OWL_NEST:
        return (
          <group position={position}>
            <mesh position={[0, -0.2, 0]}><cylinderGeometry args={[0.6, 0.5, 0.4, 8]} /><meshStandardMaterial color={COLORS.OWL_NEST} /></mesh>
            <mesh position={[0, 0.2, 0]}><sphereGeometry args={[0.35, 16, 16]} /><meshStandardMaterial color="#FFF8E1" /></mesh>
            <mesh position={[0.12, 0.3, 0.25]}><sphereGeometry args={[0.08, 6, 6]} /><meshStandardMaterial color="#212121" /></mesh>
            <mesh position={[-0.12, 0.3, 0.25]}><sphereGeometry args={[0.08, 6, 6]} /><meshStandardMaterial color="#212121" /></mesh>
          </group>
        );
      default:
        return null;
    }
  };
  
  return getDecoration();
}

function Building({ position, type, name }) {
  const getBuilding = () => {
    switch (type) {
      case 'shop':
        return (
          <group position={position}>
            <mesh position={[0, 0, 0]}><boxGeometry args={[2.5, 2, 2.5]} /><meshStandardMaterial color={COLORS.BUILDING_STONE} /></mesh>
            <mesh position={[0, 1.3, 0]}><boxGeometry args={[2.8, 0.4, 2.8]} /><meshStandardMaterial color={COLORS.ROOF} /></mesh>
            <Html position={[0, 2.2, 0]} center>
              <div style={{
                background: '#5D4037',
                color: '#FFF8E1',
                padding: '6px 10px',
                borderRadius: '6px',
                fontSize: '11px',
                fontFamily: 'system-ui, sans-serif',
                whiteSpace: 'nowrap',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
              }}>
                🏪 {name}
              </div>
            </Html>
          </group>
        );
      case 'inn':
        return (
          <group position={position}>
            <mesh position={[0, 0.4, 0]}><boxGeometry args={[2.5, 0.8, 2.5]} /><meshStandardMaterial color={COLORS.WOOD} /></mesh>
            <mesh position={[0, 1, 0]}><boxGeometry args={[2.2, 1.2, 2.2]} /><meshStandardMaterial color={COLORS.OWL_NEST} /></mesh>
            <mesh position={[0, 2, 0]}><coneGeometry args={[1.6, 1.2, 4]} /><meshStandardMaterial color={COLORS.ROOF} /></mesh>
            <Html position={[0, 2.8, 0]} center>
              <div style={{
                background: '#4E342E',
                color: '#FFF8E1',
                padding: '6px 10px',
                borderRadius: '6px',
                fontSize: '11px',
                fontFamily: 'system-ui, sans-serif'
              }}>
                🏨 {name}
              </div>
            </Html>
          </group>
        );
      case 'garden':
        return (
          <group position={position}>
            <mesh position={[0, -0.3, 0]}><boxGeometry args={[2.5, 0.3, 2.5]} /><meshStandardMaterial color={COLORS.DIRT} /></mesh>
            {[[-0.7, 0, -0.7], [0.7, 0, 0.7], [-0.7, 0, 0.7], [0.7, 0, -0.7], [0, 0, 0]].map((pos, i) => (
              <mesh key={i} position={[pos[0], pos[1] + 0.1, pos[2]]}>
                <sphereGeometry args={[0.2, 8, 8]} />
                <meshStandardMaterial color={[COLORS.FLOWER_RED, COLORS.FLOWER_YELLOW, COLORS.FLOWER_BLUE, COLORS.MUSHROOM, COLORS.FLOWER_YELLOW][i]} />
              </mesh>
            ))}
            <Html position={[0, 0.8, 0]} center>
              <div style={{
                background: '#2E7D32',
                color: '#FFF',
                padding: '6px 10px',
                borderRadius: '6px',
                fontSize: '11px',
                fontFamily: 'system-ui, sans-serif'
              }}>
                🌻 {name}
              </div>
            </Html>
          </group>
        );
      case 'mine':
        return (
          <group position={position}>
            <mesh position={[0, 0, 0]}><boxGeometry args={[2.5, 1.8, 1.2]} /><meshStandardMaterial color={COLORS.STONE} /></mesh>
            <mesh position={[0, 0.4, 0.5]}><boxGeometry args={[1.8, 1.2, 0.6]} /><meshStandardMaterial color="#1a1a1a" /></mesh>
            <mesh position={[0.5, 0.6, 0.55]}><sphereGeometry args={[0.1, 4, 4]} /><meshStandardMaterial color="#FFD700" emissive="#FFD700" emissiveIntensity={0.8} /></mesh>
            <mesh position={[-0.4, 0.7, 0.55]}><sphereGeometry args={[0.08, 4, 4]} /><meshStandardMaterial color="#FFD700" emissive="#FFD700" emissiveIntensity={0.8} /></mesh>
            <mesh position={[0.2, 0.3, 0.55]}><sphereGeometry args={[0.06, 4, 4]} /><meshStandardMaterial color="#FFD700" emissive="#FFD700" emissiveIntensity={0.6} /></mesh>
            <Html position={[0, 1.4, 0]} center>
              <div style={{
                background: '#424242',
                color: '#FFD700',
                padding: '6px 10px',
                borderRadius: '6px',
                fontSize: '11px',
                fontFamily: 'system-ui, sans-serif'
              }}>
                ⛏️ {name}
              </div>
            </Html>
          </group>
        );
      default:
        return null;
    }
  };
  
  return getBuilding();
}

function ParcelMarker({ parcel, selected, onClick }) {
  if (!parcel.forSale && !parcel.owner) return null;
  
  return (
    <mesh
      position={[parcel.centerX, parcel.y - 0.4, parcel.centerZ]}
      onClick={() => onClick(parcel)}
    >
      <boxGeometry args={[3.8, 0.1, 3.8]} />
      <meshStandardMaterial
        color={parcel.owner ? '#4CAF50' : selected ? '#FF9800' : '#2196F3'}
        transparent
        opacity={selected ? 0.6 : 0.3}
        emissive={parcel.owner ? '#4CAF50' : '#2196F3'}
        emissiveIntensity={selected ? 0.5 : 0.2}
      />
    </mesh>
  );
}

function HootWorld({ data, deployedAgents, onAgentClick, onParcelClick, selectedParcel }) {
  const { blocks, entities, parcels } = data;
  
  const blocksByType = useMemo(() => {
    const grouped = {};
    blocks.forEach(block => {
      if (!grouped[block.type]) grouped[block.type] = [];
      grouped[block.type].push(block);
    });
    return grouped;
  }, [blocks]);
  
  return (
    <group>
      {Object.entries(blocksByType).map(([type, blockList]) => (
        <InstancedBlocks
          key={type}
          blocks={blockList}
          color={BLOCK_COLORS[type]}
          transparent={TRANSPARENT_BLOCKS.has(parseInt(type))}
        />
      ))}
      
      {parcels.map(parcel => (
        <ParcelMarker
          key={parcel.id}
          parcel={parcel}
          selected={selectedParcel?.id === parcel.id}
          onClick={onParcelClick}
        />
      ))}
      
      {entities.filter(e => e.variant !== 'building').map((entity, i) => (
        <Decoration
          key={`entity-${i}`}
          position={[entity.x, entity.y, entity.z]}
          type={entity.type}
        />
      ))}
      
      {entities.filter(e => e.variant === 'building').map((entity, i) => (
        <Building
          key={`building-${i}`}
          position={[entity.x, entity.y, entity.z]}
          type={entity.buildingType}
          name={entity.name}
        />
      ))}
      
      {entities.filter(e => e.variant === 'owl_sanctuary').map((entity, i) => (
        <group key={`sanctuary-${i}`} position={[entity.x, entity.y, entity.z]}>
          <mesh position={[0, -0.2, 0]}>
            <cylinderGeometry args={[1.2, 1, 0.6, 16]} />
            <meshStandardMaterial color={COLORS.OWL_NEST} />
          </mesh>
          <mesh position={[0, 0.4, 0]}>
            <sphereGeometry args={[0.6, 16, 16]} />
            <meshStandardMaterial color="#FFF8E1" />
          </mesh>
          <mesh position={[0.2, 0.55, 0.45]}>
            <sphereGeometry args={[0.12, 8, 8]} />
            <meshStandardMaterial color="#212121" />
          </mesh>
          <mesh position={[-0.2, 0.55, 0.45]}>
            <sphereGeometry args={[0.12, 8, 8]} />
            <meshStandardMaterial color="#212121" />
          </mesh>
          <Html position={[0, 1.3, 0]} center>
            <div style={{
              background: 'rgba(93, 64, 55, 0.95)',
              color: '#FFF8E1',
              padding: '8px 14px',
              borderRadius: '8px',
              fontSize: '12px',
              fontFamily: 'system-ui, sans-serif',
              textAlign: 'center'
            }}>
              🦉 {entity.name}
              <div style={{ fontSize: '10px', marginTop: '2px', opacity: 0.8 }}>
                {entity.description}
              </div>
            </div>
          </Html>
        </group>
      ))}
      
      {deployedAgents.map(agent => (
        <OwlAgent
          key={`agent-${agent.id}`}
          agent={agent}
          onClick={onAgentClick}
        />
      ))}
    </group>
  );
}

function CameraController() {
  const { camera } = useThree();
  
  useEffect(() => {
    camera.position.set(35, 30, 35);
    camera.lookAt(0, 0, 0);
  }, [camera]);
  
  return (
    <OrbitControls
      enablePan={true}
      enableZoom={true}
      enableRotate={true}
      minDistance={5}
      minPolarAngle={0.1}
      maxPolarAngle={Math.PI / 2 - 0.1}
      target={[0, 2, 0]}
      enableDamping={true}
      dampingFactor={0.05}
    />
  );
}

// ============================================
// WALLET & NFT LOGIC
// ============================================

const TIER_NAMES = ['Agent', 'Sentinel', 'Overseer', 'Elder'];

// Fetch agents using balanceOf + fallback to scanning if tokenOfOwnerByIndex fails
async function fetchHootlandAgents(address, provider) {
  if (!address || !provider) return [];
  
  try {
    const contract = new ethers.Contract(HOOTLAND_AGENT_ADDRESS, HOOTLAND_AGENT_ABI, provider);
    const agentsList = [];
    
    // Get user's balance
    const balance = await contract.balanceOf(address);
    const count = Number(balance);
    
    console.log(`🦉 Found ${count} agents for wallet ${address.slice(0,6)}...`);
    
    if (count === 0) {
      return [];
    }
    
    // Try tokenOfOwnerByIndex first (standard ERC721Enumerable)
    let useEnumeration = true;
    
    for (let i = 0; i < count; i++) {
      try {
        let tokenId;
        
        if (useEnumeration) {
          try {
            tokenId = await contract.tokenOfOwnerByIndex(address, i);
          } catch (e) {
            // Enumeration not supported, fall back to scanning
            console.log('Enumeration not supported, falling back to scanning...');
            useEnumeration = false;
            
            // Get total supply for scanning
            const supply = await contract.genesisSupply();
            const total = Number(supply);
            
            // Scan all tokens to find owned ones
            for (let j = 1; j <= total; j++) {
              try {
                const owner = await contract.ownerOf(j);
                if (owner.toLowerCase() === address.toLowerCase()) {
                  // Found one, add it
                  const agentData = await contract.agents(j);
                  agentsList.push({
                    id: j,
                    agentId: Number(agentData[0]),
                    agentType: Number(agentData[1]),
                    tier: Number(agentData[2]) + 1,
                    multiplier: Number(agentData[3]),
                    x: Number(agentData[6]),
                    y: Number(agentData[7]),
                    lastClaimTime: Number(agentData[8]),
                    totalEarned: ethers.formatEther(agentData[9]),
                    active: agentData[11],
                    earnings: (10 * (Number(agentData[3]) / 100))
                  });
                }
              } catch (e2) {
                // Token doesn't exist, skip
              }
            }
            break; // Exit the outer loop
          }
        }
        
        if (useEnumeration && tokenId) {
          // Get agent data using getAgentInfo (correct function name)
          const agentData = await contract.getAgentInfo(tokenId);
          
          agentsList.push({
            id: Number(tokenId),
            agentId: Number(agentData.agentId),
            agentType: Number(agentData.agentType),
            tier: Number(agentData.tier) + 1,
            multiplier: Number(agentData.multiplier),
            x: Number(agentData.x),
            y: Number(agentData.y),
            lastClaimTime: Number(agentData.lastClaimTime),
            totalEarned: ethers.formatEther(agentData.totalEarned),
            active: agentData.active,
            earnings: (10 * (Number(agentData.multiplier) / 100))
          });
        }
        
      } catch (e) {
        console.log(`Token at index ${i} error:`, e.message);
      }
    }
    
    console.log('✅ Fetched agents:', agentsList);
    return agentsList;
    
  } catch (e) {
    console.error('Error fetching agents:', e);
    return [];
  }
}

// ============================================
// MAIN COMPONENT
// ============================================

export default function HootHollow() {
  const [worldData, setWorldData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Wallet state
  const [wallet, setWallet] = useState(null);
  const [walletBalance, setWalletBalance] = useState(null);
  const [agents, setAgents] = useState([]);
  const [deployedAgents, setDeployedAgents] = useState([]);
  const [owlSDTBalance, setOwlSDTBalance] = useState('0');
  const [loadingAgents, setLoadingAgents] = useState(false);
  
  // UI state
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedParcel, setSelectedParcel] = useState(null);
  const [showDeployModal, setShowDeployModal] = useState(false);
  const [error, setError] = useState(null);
  
  // Generate world on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      const data = generateHootHollow();
      setWorldData(data);
      setLoading(false);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);
  
  // Connect wallet with Base network check
  const connectWallet = async () => {
    if (!window.ethereum) {
      setError('Please install MetaMask to use Hoot Hollow');
      return;
    }
    
    try {
      setError(null);
      
      // Request accounts first
      const accounts = await window.ethereum.request({
        method: 'eth_requestAccounts'
      });
      
      if (accounts.length === 0) {
        throw new Error('No accounts found');
      }
      
      const address = accounts[0];
      
      // Check network and switch to Base if needed
      const chainId = await window.ethereum.request({ method: 'eth_chainId' });
      const currentChain = parseInt(chainId, 16);
      
      console.log('Current chain:', currentChain, '(Base is 8453)');
      
      if (currentChain !== BASE_CHAIN_ID) {
        console.log('Switching to Base network...');
        try {
          await window.ethereum.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: BASE_CHAIN_ID_HEX }]
          });
        } catch (switchError) {
          // If Base not added, add it
          if (switchError.code === 4902) {
            await window.ethereum.request({
              method: 'wallet_addEthereumChain',
              params: [{
                chainId: BASE_CHAIN_ID_HEX,
                chainName: 'Base Mainnet',
                nativeCurrency: { name: 'ETH', symbol: 'ETH', decimals: 18 },
                rpcUrls: [BASE_RPC_URL],
                blockExplorerUrls: ['https://basescan.org']
              }]
            });
          } else {
            throw switchError;
          }
        }
      }
      
      // Now create provider and get data
      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      const balance = await provider.getBalance(address);
      
      setWallet({ address, provider, signer });
      setWalletBalance(ethers.formatEther(balance));
      
      // Fetch agents
      setLoadingAgents(true);
      const userAgents = await fetchHootlandAgents(address, provider);
      setAgents(userAgents);
      setDeployedAgents(userAgents.filter(a => a.active));
      setLoadingAgents(false);
      
      console.log('✅ Connected to Base and fetched', userAgents.length, 'agents');
      
    } catch (e) {
      console.error('Wallet connection error:', e);
      setError(e.message || 'Failed to connect wallet');
    }
  };
  
  // Disconnect wallet
  const disconnectWallet = () => {
    setWallet(null);
    setWalletBalance(null);
    setAgents([]);
    setDeployedAgents([]);
    setSelectedAgent(null);
    setSelectedParcel(null);
  };
  
  // Deploy agent to world
  const deployAgent = async (agent, parcel) => {
    if (!wallet || !parcel) return;
    
    try {
      setError(null);
      const updatedAgent = {
        ...agent,
        x: parcel.centerX,
        y: parcel.y,
        z: parcel.centerZ,
        active: true,
        parcelId: parcel.id
      };
      
      setDeployedAgents(prev => [...prev.filter(a => a.id !== agent.id), updatedAgent]);
      setAgents(prev => prev.map(a => a.id === agent.id ? updatedAgent : a));
      setShowDeployModal(false);
      setSelectedParcel(null);
      
    } catch (e) {
      console.error('Deploy error:', e);
      setError('Failed to deploy agent');
    }
  };
  
  // Handle parcel click
  const handleParcelClick = (parcel) => {
    if (!wallet) {
      setError('Connect your wallet to claim parcels');
      return;
    }
    setSelectedParcel(parcel);
    if (selectedAgent) {
      setShowDeployModal(true);
    }
  };
  
  // Handle agent click
  const handleAgentClick = (agent) => {
    setSelectedAgent(agent);
    if (selectedParcel) {
      setShowDeployModal(true);
    }
  };
  
  if (loading) {
    return (
      <div style={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(180deg, #87CEEB 0%, #E0F6FF 100%)',
        fontFamily: 'system-ui, sans-serif',
        color: '#5D4037'
      }}>
        <div style={{ fontSize: '64px', marginBottom: '20px' }}>🦉</div>
        <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '10px' }}>
          Building Hoot Hollow...
        </div>
        <div style={{ fontSize: '14px', opacity: 0.7 }}>
          A cozy woodland sanctuary
        </div>
      </div>
    );
  }
  
  return (
    <div style={{ width: '100vw', height: '100vh', background: '#87CEEB' }}>
      <Canvas
        camera={{ fov: 60, near: 0.1, far: 1000 }}
        style={{ background: 'linear-gradient(180deg, #87CEEB 0%, #E0F6FF 100%)' }}
      >
        <ambientLight intensity={0.6} color="#FFF8E1" />
        <directionalLight position={[50, 100, 50]} intensity={1.2} color="#FFE4B5" />
        <hemisphereLight skyColor="#87CEEB" groundColor="#7CB342" intensity={0.4} />
        <Sky distance={450000} sunPosition={[100, 50, 100]} inclination={0.5} azimuth={0.25} rayleigh={0.5} />
        
        {worldData && (
          <HootWorld
            data={worldData}
            deployedAgents={deployedAgents}
            onAgentClick={handleAgentClick}
            onParcelClick={handleParcelClick}
            selectedParcel={selectedParcel}
          />
        )}
        
        <CameraController />
      </Canvas>
      
      {/* Top-left info panel */}
      <div style={{
        position: 'absolute',
        top: '20px',
        left: '20px',
        background: 'rgba(255, 248, 225, 0.95)',
        padding: '16px 20px',
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        fontFamily: 'system-ui, sans-serif',
        maxWidth: '300px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
          <span style={{ fontSize: '28px' }}>🦉</span>
          <div>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#5D4037' }}>Hoot Hollow</div>
            <div style={{ fontSize: '11px', color: '#8D6E63' }}>A cozy woodland sanctuary</div>
          </div>
        </div>
        
        {!wallet ? (
          <div>
            <button
              onClick={connectWallet}
              style={{
                background: '#4CAF50',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 20px',
                fontSize: '14px',
                fontWeight: 'bold',
                cursor: 'pointer',
                width: '100%',
                marginBottom: '8px'
              }}
            >
              🦉 Connect Wallet
            </button>
            <div style={{ fontSize: '11px', color: '#8D6E63', textAlign: 'center' }}>
              Connect your Hootland agents to enter
            </div>
          </div>
        ) : (
          <div>
            <div style={{ fontSize: '12px', color: '#5D4037', marginBottom: '8px' }}>
              <div style={{ marginBottom: '4px' }}>
                <strong>Address:</strong> {wallet.address.slice(0, 6)}...{wallet.address.slice(-4)}
              </div>
              <div style={{ marginBottom: '4px' }}>
                <strong>Agents:</strong> {agents.length} 🦉
              </div>
              <div>
                <strong>Deployed:</strong> {deployedAgents.length}
              </div>
            </div>
            <button
              onClick={disconnectWallet}
              style={{
                background: 'transparent',
                color: '#8D6E63',
                border: '1px solid #8D6E63',
                borderRadius: '6px',
                padding: '6px 12px',
                fontSize: '11px',
                cursor: 'pointer',
                width: '100%'
              }}
            >
              Disconnect
            </button>
          </div>
        )}
        
        {error && (
          <div style={{
            marginTop: '10px',
            padding: '8px 12px',
            background: '#FFEBEE',
            borderRadius: '6px',
            fontSize: '11px',
            color: '#C62828'
          }}>
            ⚠️ {error}
          </div>
        )}
      </div>
      
      {/* Agents panel */}
      {wallet && agents.length > 0 && (
        <div style={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          background: 'rgba(255, 248, 225, 0.95)',
          padding: '16px',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          fontFamily: 'system-ui, sans-serif',
          maxWidth: '240px',
          maxHeight: '400px',
          overflowY: 'auto'
        }}>
          <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#5D4037', marginBottom: '10px' }}>
            🦉 Your Agents
          </div>
          
          {loadingAgents ? (
            <div style={{ fontSize: '12px', color: '#8D6E63' }}>Loading...</div>
          ) : (
            agents.map(agent => (
              <div
                key={agent.id}
                onClick={() => handleAgentClick(agent)}
                style={{
                  padding: '10px',
                  marginBottom: '8px',
                  background: selectedAgent?.id === agent.id ? '#E8F5E9' : agent.active ? '#FFF3E0' : '#F5F5F5',
                  borderRadius: '8px',
                  border: selectedAgent?.id === agent.id ? '2px solid #4CAF50' : '1px solid #E0E0E0',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                  <span style={{ fontWeight: 'bold', fontSize: '12px' }}>
                    Agent #{agent.id}
                  </span>
                  <span style={{
                    fontSize: '10px',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    background: TIER_COLORS[agent.tier] || TIER_COLORS[1],
                    color: '#fff'
                  }}>
                    {['Agent', 'Sentinel', 'Overseer', 'Elder'][agent.tier - 1] || 'Agent'}
                  </span>
                </div>
                <div style={{ fontSize: '11px', color: '#666' }}>
                  ⚡ {agent.earnings.toFixed(2)} owlSDT/hr
                </div>
                {agent.active ? (
                  <div style={{ fontSize: '10px', color: '#4CAF50', marginTop: '4px' }}>
                    🟢 Deployed in world
                  </div>
                ) : (
                  <div style={{ fontSize: '10px', color: '#FF9800', marginTop: '4px' }}>
                    🟠 Ready to deploy
                  </div>
                )}
              </div>
            ))
          )}
          
          <div style={{ fontSize: '10px', color: '#8D6E63', marginTop: '10px', borderTop: '1px solid #E0E0E0', paddingTop: '10px' }}>
            Click agent, then click a blue parcel to deploy
          </div>
        </div>
      )}
      
      {/* Deploy Modal */}
      {showDeployModal && selectedAgent && selectedParcel && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'rgba(255, 248, 225, 0.98)',
          padding: '24px',
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          fontFamily: 'system-ui, sans-serif',
          maxWidth: '360px',
          zIndex: 1000
        }}>
          <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#5D4037', marginBottom: '16px', textAlign: 'center' }}>
            🦉 Deploy Agent
          </div>
          
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
              You are about to deploy:
            </div>
            <div style={{ background: '#FFF', padding: '12px', borderRadius: '8px', marginBottom: '12px' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                Agent #{selectedAgent.id} ({['Agent', 'Sentinel', 'Overseer', 'Elder'][selectedAgent.tier - 1]})
              </div>
              <div style={{ fontSize: '12px', color: '#4CAF50' }}>
                ⚡ Earns {selectedAgent.earnings.toFixed(2)} owlSDT/hour
              </div>
            </div>
            
            <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
              To parcel:
            </div>
            <div style={{ background: '#FFF', padding: '12px', borderRadius: '8px' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                Parcel #{selectedParcel.id}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                Location: ({selectedParcel.centerX}, {selectedParcel.centerZ})
              </div>
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => { setShowDeployModal(false); setSelectedParcel(null); setSelectedAgent(null); }}
              style={{
                flex: 1,
                background: '#E0E0E0',
                color: '#666',
                border: 'none',
                borderRadius: '8px',
                padding: '12px',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              onClick={() => deployAgent(selectedAgent, selectedParcel)}
              style={{
                flex: 1,
                background: '#4CAF50',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                padding: '12px',
                fontSize: '14px',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              🦉 Deploy
            </button>
          </div>
        </div>
      )}
      
      {/* Controls hint */}
      <div style={{
        position: 'absolute',
        bottom: '20px',
        left: '20px',
        background: 'rgba(0,0,0,0.6)',
        color: '#fff',
        padding: '10px 14px',
        borderRadius: '8px',
        fontSize: '12px',
        fontFamily: 'system-ui, sans-serif'
      }}>
        🖱️ Drag to rotate • Scroll to zoom • Right-drag to pan
      </div>
      
      {/* Land legend */}
      <div style={{
        position: 'absolute',
        bottom: '20px',
        right: '20px',
        background: 'rgba(0,0,0,0.6)',
        color: '#fff',
        padding: '10px 14px',
        borderRadius: '8px',
        fontSize: '11px',
        fontFamily: 'system-ui, sans-serif'
      }}>
        <div style={{ marginBottom: '4px' }}>📍 Land Status:</div>
        <div style={{ color: '#2196F3' }}>⬜ Blue = For Sale</div>
        <div style={{ color: '#4CAF50' }}>🟢 Green = Owned</div>
        <div style={{ color: '#FF9800' }}>🟠 Orange = Selected</div>
      </div>
    </div>
  );
}
